import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, AlertCircle, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import logoGWPC from '../../assets/logo/gwpc-logo-transparent.png';

/* Fresh Odoo-style reset-password screen, matching Login.jsx's clean
   centered-card look (theme.css tokens: --bg, --surface, --line, --accent,
   etc.) instead of the old two-column marketing layout (Bahrain flag photo,
   sunset gradient, feature list, separate desktop/mobile branches). All
   state/handlers (email, loading, message, error, handleSubmit) are
   unchanged. */
const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setMessage('If an account with that email exists, we have sent a password reset link.');
    } catch (err) {
      setError('Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Shared field styling -- same recipe as Login.jsx.
  const fieldClass =
    'h-[42px] w-full rounded-lg border bg-white text-[15px] outline-none transition-all duration-150 placeholder:text-[#9aa7ba] focus:ring-[3px]';
  const fieldStyle = { borderColor: 'var(--line-3)' };

  return (
    <div
      className="flex min-h-screen w-full items-center justify-center px-4 py-10"
      style={{ background: 'var(--bg)' }}
    >
      <div className="w-full max-w-[400px]">
        {/* Brand */}
        <div className="mb-7 flex flex-col items-center text-center">
          <img
            src={logoGWPC}
            alt="Great Workplace Club"
            className="h-auto w-[190px] object-contain"
          />
          <p className="mt-3 text-[13px]" style={{ color: 'var(--muted)' }}>
            Employee Engagement &amp; Survey Platform
          </p>
        </div>

        {/* Card */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="rounded-xl p-7"
          style={{ background: 'var(--surface)', border: '1px solid var(--line)', boxShadow: 'var(--shadow-sm)' }}
        >
          <div className="mb-5 flex items-center gap-2">
            <Link
              to="/login"
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-colors"
              style={{ color: 'var(--muted)' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--hover)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              aria-label="Back to login"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <h2 className="text-[20px] font-semibold" style={{ color: 'var(--ink)' }}>Reset your password</h2>
          </div>

          <p className="mb-5 text-[13px] leading-[1.4]" style={{ color: 'var(--muted)' }}>
            Enter your email address and we&apos;ll send you a link to reset your password.
          </p>

          {message && (
            <div className="mb-4 flex items-center rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
              <CheckCircle2 className="mr-2 h-5 w-5 flex-shrink-0" />{message}
            </div>
          )}

          {error && (
            <div className="mb-4 flex items-center rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle className="mr-2 h-5 w-5 flex-shrink-0" />{error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-1 block text-[12px] font-medium" style={{ color: 'var(--body-2)' }}>Email address</label>
              <div className="group relative">
                <Mail
                  className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2"
                  style={{ color: 'var(--faint)' }}
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`${fieldClass} pl-12 pr-4`}
                  style={{ ...fieldStyle, '--tw-ring-color': 'var(--accent)' }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--line-3)'; }}
                  placeholder="Enter your email address"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="flex h-[46px] w-full items-center justify-center gap-3 rounded-lg text-[15px] font-semibold text-white transition-all duration-150 disabled:pointer-events-none disabled:opacity-50"
              style={{ background: 'var(--accent)' }}
              onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = 'var(--accent-strong)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--accent)'; }}
            >
              {loading ? (
                <><div className="h-5 w-5 animate-spin rounded-full border-b-2 border-white" />Sending...</>
              ) : (
                'Send reset link'
              )}
            </button>
          </form>

          <div className="mt-5 text-center text-[13px]" style={{ borderTop: '1px solid var(--line)', paddingTop: '1rem', color: 'var(--body)' }}>
            <Link to="/login" className="font-medium" style={{ color: 'var(--accent)' }}>
              Back to login
            </Link>
          </div>
        </motion.div>

        {/* Footer */}
        <div className="mt-6 flex items-center justify-center gap-3 text-[12px]" style={{ color: 'var(--faint)' }}>
          <Link to="/contact" className="hover:underline">Privacy Policy</Link>
          <span>|</span>
          <Link to="/contact" className="hover:underline">Terms of Service</Link>
          <span>|</span>
          <Link to="/contact" className="hover:underline">Help &amp; Support</Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
